import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { HardHat, LogOut, Plus, Trash2, FileDown, Layers, Loader2, GitCompare, X, Share2, StickyNote, Check } from "lucide-react";
import { Button } from "../components/ui/button";
import { useAuth } from "../context/AuthContext";
import { listProjects, deleteProject, renderImageUrl, createShare, updateProject } from "../lib/api";
import { exportEstimatePDF } from "../lib/pdf";
import { useToast } from "../hooks/use-toast";

const money = (n, cur) => `${cur === "CAD" ? "CA$" : "$"}${Math.round(n || 0).toLocaleString("en-US")}`;

const NotesEditor = ({ project, onToast }) => {
  const [open, setOpen] = useState(false);
  const [notes, setNotes] = useState(project.notes || "");
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  const save = async () => {
    setSaving(true);
    setSaved(false);
    try {
      await updateProject(project.id, { notes });
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (e) {
      onToast({ title: "Couldn't save notes" });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mt-3 border-t border-slate-100 pt-3">
      <button
        onClick={() => setOpen((o) => !o)}
        className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-[#1D4ED8] transition-colors"
      >
        <StickyNote className="h-4 w-4" />
        {project.notes ? "Contractor notes" : "Add contractor notes"}
        {project.notes && !open && <span className="text-xs text-slate-400 truncate max-w-[120px]">· {project.notes}</span>}
      </button>
      {open && (
        <div className="mt-2">
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={3}
            placeholder="Jot decisions, questions or reminders before sharing…"
            className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-[#1D4ED8]/40"
          />
          <button
            onClick={save}
            disabled={saving}
            className="mt-2 flex items-center gap-1.5 rounded-md bg-[#1D4ED8] hover:bg-[#1e40af] text-white text-xs font-semibold px-3 py-1.5 disabled:opacity-60"
          >
            {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : saved ? <Check className="h-3.5 w-3.5" /> : null}
            {saved ? "Saved" : "Save notes"}
          </button>
        </div>
      )}
    </div>
  );
};

const Dashboard = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState([]);

  const load = async () => {
    try {
      const data = await listProjects();
      setProjects(data);
    } catch (e) {
      toast({ title: "Couldn't load projects", description: "Please refresh and try again." });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const handleLogout = async () => { await logout(); navigate("/"); };

  const remove = async (id) => {
    try {
      await deleteProject(id);
      setProjects((p) => p.filter((x) => x.id !== id));
      setSelected((s) => s.filter((x) => x !== id));
      toast({ title: "Project deleted" });
    } catch (e) {
      toast({ title: "Delete failed" });
    }
  };

  const share = async (p) => {
    try {
      const { slug } = await createShare({
        name: p.name,
        request: p.request,
        result: p.result,
        render_id: p.render_id || undefined,
      });
      const url = `${window.location.origin}/share/${slug}`;
      try {
        await navigator.clipboard.writeText(url);
        toast({ title: "Share link copied!", description: url });
      } catch (e) {
        toast({ title: "Share link ready", description: url });
      }
    } catch (e) {
      toast({ title: "Couldn't create link" });
    }
  };

  const toggleSelect = (id) => {
    setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : s.length < 3 ? [...s, id] : s));
  };

  const compareList = projects.filter((p) => selected.includes(p.id));

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-white border-b border-slate-200 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 h-[68px] flex items-center justify-between">
          <button onClick={() => navigate("/")} className="flex items-center gap-2.5">
            <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-[#1D4ED8] text-white">
              <HardHat className="h-5 w-5" />
            </span>
            <span className="font-display text-xl font-700 text-[#0A2540]">
              cost<span className="text-[#DC2626]">2</span>build<span className="text-[#1D4ED8]">.ai</span>
            </span>
          </button>
          <div className="flex items-center gap-3">
            <Button onClick={() => navigate("/")} className="bg-[#DC2626] hover:bg-[#b91c1c] text-white">
              <Plus className="h-4 w-4 mr-2" /> New estimate
            </Button>
            <div className="flex items-center gap-2">
              {user?.picture ? (
                <img src={user.picture} alt={user.name} className="h-9 w-9 rounded-full ring-2 ring-blue-100" referrerPolicy="no-referrer" />
              ) : (
                <span className="flex h-9 w-9 items-center justify-center rounded-full bg-[#1D4ED8] text-white font-semibold">{user?.name?.charAt(0)}</span>
              )}
              <button onClick={handleLogout} className="text-slate-500 hover:text-[#DC2626]" title="Log out">
                <LogOut className="h-5 w-5" />
              </button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-5 sm:px-8 py-10">
        <div className="flex items-end justify-between flex-wrap gap-4">
          <div>
            <h1 className="font-display text-3xl font-700 text-[#0A2540]">Welcome back, {user?.name?.split(" ")[0]}</h1>
            <p className="mt-1 text-slate-600">{projects.length} saved {projects.length === 1 ? "project" : "projects"} · select up to 3 to compare.</p>
          </div>
        </div>

        {/* Comparison */}
        {compareList.length >= 2 && (
          <div className="mt-8 rounded-2xl border border-slate-200 bg-white overflow-hidden">
            <div className="flex items-center gap-2 px-6 py-4 border-b border-slate-100 bg-slate-50">
              <GitCompare className="h-5 w-5 text-[#1D4ED8]" />
              <span className="font-semibold text-[#0A2540]">Comparing {compareList.length} projects</span>
              <button onClick={() => setSelected([])} className="ml-auto text-sm text-slate-500 hover:text-[#DC2626] flex items-center gap-1">
                <X className="h-4 w-4" /> Clear
              </button>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-slate-500">
                    <th className="px-6 py-3 font-medium">Metric</th>
                    {compareList.map((p) => (<th key={p.id} className="px-6 py-3 font-semibold text-[#0A2540]">{p.name}</th>))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {[
                    ["Type", (p) => p.request?.building_type],
                    ["Size", (p) => `${p.request?.size_sqft} sq ft`],
                    ["Finish", (p) => p.request?.finish_level],
                    ["Region", (p) => p.request?.region],
                    ["Low", (p) => money(p.result?.total_low, p.result?.currency)],
                    ["High", (p) => money(p.result?.total_high, p.result?.currency)],
                  ].map(([label, fn]) => (
                    <tr key={label}>
                      <td className="px-6 py-3 text-slate-500">{label}</td>
                      {compareList.map((p) => (<td key={p.id} className="px-6 py-3 text-[#0A2540] font-medium">{fn(p)}</td>))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Projects grid */}
        {loading ? (
          <div className="mt-16 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-[#1D4ED8]" /></div>
        ) : projects.length === 0 ? (
          <div className="mt-16 text-center rounded-2xl border border-dashed border-slate-300 py-20">
            <Layers className="h-10 w-10 mx-auto text-slate-300" />
            <p className="mt-4 text-slate-600">No saved projects yet.</p>
            <Button onClick={() => navigate("/")} className="mt-4 bg-[#DC2626] hover:bg-[#b91c1c] text-white">Create your first estimate</Button>
          </div>
        ) : (
          <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {projects.map((p) => (
              <div key={p.id} className={`rounded-2xl border bg-white overflow-hidden transition-all ${selected.includes(p.id) ? "border-[#DC2626] shadow-md" : "border-slate-200 hover:shadow-md"}`}>
                <div className="h-40 bg-slate-100 relative">
                  {p.render_id ? (
                    <img src={renderImageUrl(p.render_id)} alt={p.name} className="h-full w-full object-cover" />
                  ) : (
                    <div className="h-full w-full flex items-center justify-center text-slate-300"><Layers className="h-8 w-8" /></div>
                  )}
                  <label className="absolute top-3 left-3 flex items-center gap-1.5 rounded-full bg-white/90 px-2.5 py-1 text-xs font-medium cursor-pointer">
                    <input type="checkbox" checked={selected.includes(p.id)} onChange={() => toggleSelect(p.id)} className="accent-[#DC2626]" />
                    Compare
                  </label>
                </div>
                <div className="p-5">
                  <h3 className="font-display text-lg font-600 text-[#0A2540] truncate">{p.name}</h3>
                  <p className="mt-1 text-sm text-slate-500">{p.request?.building_type} · {p.request?.size_sqft} sq ft</p>
                  <div className="mt-3 font-display text-xl font-700 text-[#0A2540]">
                    {money(p.result?.total_low, p.result?.currency)} – {money(p.result?.total_high, p.result?.currency)}
                  </div>
                  <div className="mt-4 flex gap-2">
                    <Button variant="outline" onClick={() => exportEstimatePDF({ request: p.request, result: p.result, name: p.name })} className="flex-1 border-slate-300 text-[#0A2540] hover:border-[#1D4ED8] hover:text-[#1D4ED8]">
                      <FileDown className="h-4 w-4 mr-1.5" /> PDF
                    </Button>
                    <Button variant="outline" onClick={() => share(p)} className="border-slate-300 text-[#0A2540] hover:border-[#1D4ED8] hover:text-[#1D4ED8] px-3">
                      <Share2 className="h-4 w-4" />
                    </Button>
                    <Button variant="outline" onClick={() => remove(p.id)} className="border-slate-300 text-slate-500 hover:border-[#DC2626] hover:text-[#DC2626] px-3">
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                  <NotesEditor project={p} onToast={toast} />
                </div>
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
};

export default Dashboard;
